import { create } from 'zustand'
import {
  Activation,
  ActivationStatus,
  ActivationSummary,
  ActivationPagination,
  CreateActivationRequest,
  UpdateActivationRequest
} from '../types/activation'
import { activationService } from '../services/activationService'

interface ActivationState {
  // State
  activations: Activation[]
  summary: ActivationSummary | null
  pagination: ActivationPagination | null
  loading: boolean
  error: string | null
  selectedActivation: Activation | null

  // Actions
  loadActivations: (
    customerId: string,
    search?: string,
    partnerId?: string,
    activationTypeId?: string,
    status?: ActivationStatus,
    page?: number,
    limit?: number
  ) => Promise<void>
  loadActivation: (id: string, customerId: string) => Promise<void>
  createActivation: (customerId: string, data: CreateActivationRequest) => Promise<Activation>
  updateActivation: (
    id: string,
    customerId: string,
    data: UpdateActivationRequest
  ) => Promise<Activation>
  deleteActivation: (id: string, customerId: string) => Promise<void>
  setActivationAssets: (id: string, customerId: string, assetIds: string[]) => Promise<Activation>
  removeActivationAsset: (id: string, customerId: string, assetId: string) => Promise<void>
  setSelectedActivation: (activation: Activation | null) => void
  reset: () => void
}

const useActivationStore = create<ActivationState>((set) => ({
  // Initial State
  activations: [],
  summary: null,
  pagination: null,
  loading: false,
  error: null,
  selectedActivation: null,

  // Actions
  loadActivations: async (
    customerId: string,
    search?: string,
    partnerId?: string,
    activationTypeId?: string,
    status?: ActivationStatus,
    page?: number,
    limit?: number
  ) => {
    set({ loading: true, error: null })
    try {
      const { data, summary, pagination } = await activationService.getActivations(
        customerId,
        search,
        partnerId,
        activationTypeId,
        status,
        page,
        limit
      )
      set({ activations: data, summary, pagination, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load activations',
        loading: false
      })
    }
  },

  loadActivation: async (id: string, customerId: string) => {
    set({ loading: true, error: null })
    try {
      const activation = await activationService.getActivation(id, customerId)
      set({ selectedActivation: activation, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load activation',
        loading: false
      })
    }
  },

  createActivation: async (customerId: string, data: CreateActivationRequest) => {
    const activation = await activationService.createActivation(customerId, data)
    // Note: We don't add to local state here because the API response may not include
    // the full nested objects needed for display.
    // The ActivationsList component will reload activations after successful creation.
    return activation
  },

  updateActivation: async (id: string, customerId: string, data: UpdateActivationRequest) => {
    const activation = await activationService.updateActivation(id, customerId, data)
    // Note: We don't update local state here because the API response may not include
    // the full nested objects needed for display.
    // The ActivationsList component will reload activations after successful update.
    return activation
  },

  deleteActivation: async (id: string, customerId: string) => {
    await activationService.deleteActivation(id, customerId)

    // Remove from local state - safe for delete since no nested objects needed
    set((state) => ({
      activations: state.activations.filter((activation) => activation.id !== id),
      selectedActivation:
        state.selectedActivation?.id === id ? null : state.selectedActivation
    }))
  },

  setActivationAssets: async (id: string, customerId: string, assetIds: string[]) => {
    const activation = await activationService.setActivationAssets(id, customerId, assetIds)
    // Note: We don't update local state here because the API response may not include
    // the full nested objects needed for display.
    return activation
  },

  removeActivationAsset: async (id: string, customerId: string, assetId: string) => {
    await activationService.removeActivationAsset(id, customerId, assetId)
    // Note: We don't update local state here - caller should reload if needed
  },

  setSelectedActivation: (activation: Activation | null) => {
    set({ selectedActivation: activation })
  },

  reset: () =>
    set({
      activations: [],
      summary: null,
      pagination: null,
      loading: false,
      error: null,
      selectedActivation: null
    })
}))

export default useActivationStore
